import React from 'react';
import PropTypes from 'prop-types';

import { FormattedMessage } from '../../util/reactIntl';
import EmptyState from '../EmptyState/EmptyState';

import css from './DataState.module.css';

/**
 * DataState — makes a data view say which of three things is true.
 *
 * The problem it solves: the Statistik page and Ekonomi → Hantera betalningar
 * both render pure white space when they fail, which looks exactly like
 * "you have no data yet". A seller cannot tell a broken page from an empty one,
 * so they wait for data that is never coming. See P25009-387.
 *
 * The three states are mutually exclusive and always explicit:
 *
 *   loading  → a skeleton placeholder shaped like the content that is coming
 *   error    → what happened, a Retry button, and a route to support
 *   empty    → why it is empty and what to do next
 *   ...else  → `children`
 *
 * Usage:
 *
 *   <DataState
 *     loading={fetchInProgress}
 *     error={fetchError}
 *     isEmpty={rows.length === 0}
 *     onRetry={reload}
 *     skeleton="cards"
 *     empty={{ title: 'Ingen försäljning ännu', lead: '…', action: {…} }}
 *   >
 *     <Table rows={rows} />
 *   </DataState>
 *
 * Precedence is loading → error → empty. Error beats empty on purpose: a failed
 * fetch leaves the collection empty too, and reporting that as "no data" is the
 * exact confusion this component exists to remove.
 */

const HELP_PATH = '/p/kontakt';

/* Skeleton shapes. Each is a plain arrangement of shimmering blocks — no text,
 * because a placeholder that reads "Laddar…" competes with the real content for
 * the same space and makes the swap feel like a page change. */
const SKELETON_SHAPES = {
  /* A row of stat cards, as on the Ekonomi overview. */
  cards: count => (
    <div className={css.skeletonCards}>
      {Array.from({ length: count ?? 4 }, (_, i) => (
        <div key={i} className={css.skeletonCard}>
          <span className={`${css.shimmer} ${css.skeletonCardLabel}`} />
          <span className={`${css.shimmer} ${css.skeletonCardValue}`} />
        </div>
      ))}
    </div>
  ),

  /* A list or table. */
  rows: count => (
    <div className={css.skeletonRows}>
      {Array.from({ length: count ?? 5 }, (_, i) => (
        <div key={i} className={css.skeletonRow}>
          <span className={`${css.shimmer} ${css.skeletonRowThumb}`} />
          <span className={css.skeletonRowText}>
            <span className={`${css.shimmer} ${css.skeletonRowLine}`} />
            <span className={`${css.shimmer} ${css.skeletonRowLineShort}`} />
          </span>
        </div>
      ))}
    </div>
  ),

  /* One large area, e.g. an embedded dashboard or a chart. */
  block: () => <div className={`${css.shimmer} ${css.skeletonBlock}`} />,
};

export const DataStateSkeleton = ({ variant = 'rows', count, label }) => {
  const shape = SKELETON_SHAPES[variant] || SKELETON_SHAPES.rows;
  return (
    /* aria-busy + a visually hidden label so a screen reader announces that
     * something is loading — the shimmer alone conveys nothing to it. */
    <div className={css.skeleton} aria-busy="true" aria-live="polite">
      <span className={css.srOnly}>
        {label ?? <FormattedMessage id="DataState.loading" />}
      </span>
      {shape(count)}
    </div>
  );
};

const WarningIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const DataStateError = ({ title, lead, onRetry, helpPath = HELP_PATH, detail }) => (
  <div className={css.error} role="alert">
    <div className={css.errorIcon}>
      <WarningIcon />
    </div>
    <p className={css.errorTitle}>{title ?? <FormattedMessage id="DataState.errorTitle" />}</p>
    <p className={css.errorLead}>{lead ?? <FormattedMessage id="DataState.errorLead" />}</p>
    {/* The raw message is kept, collapsed, rather than dropped: support cannot
      * act on "something went wrong", and the seller should not have to open a
      * browser console to find out what failed. */}
    {detail ? (
      <details className={css.errorDetails}>
        <summary>
          <FormattedMessage id="DataState.errorDetailsToggle" />
        </summary>
        <code className={css.errorDetailText}>{detail}</code>
      </details>
    ) : null}
    <div className={css.errorActions}>
      {onRetry ? (
        <button type="button" className={css.retryButton} onClick={onRetry}>
          <FormattedMessage id="DataState.retry" />
        </button>
      ) : null}
      <a className={css.helpLink} href={helpPath}>
        <FormattedMessage id="DataState.getHelp" />
      </a>
    </div>
  </div>
);

/**
 * Pull something human out of whatever shape the failure arrived in.
 *
 * A Sharetribe failure carries its real reason in apiErrors (code + title) and
 * an empty `message`, which used to leave "Teknisk information" blank — the
 * box support asks sellers to read out (P25009-512 BR-12). The code comes
 * first because it is what support can search for; the status is the last
 * resort, so a failure always shows something.
 */
export const errorDetailOf = error => {
  if (!error || error === true) return null;
  if (typeof error === 'string') return error;
  const apiErrors = error?.apiErrors;
  if (Array.isArray(apiErrors) && apiErrors.length) {
    const first = apiErrors[0];
    const parts = [first?.code, first?.title].filter(Boolean);
    if (parts.length) return [...new Set(parts)].join(' — ');
  }
  if (error?.message) return error.message;
  const status = [error?.status, error?.statusText].filter(Boolean).join(' ');
  return status || error?.name || null;
};

const DataState = props => {
  const {
    loading = false,
    error = null,
    isEmpty = false,
    onRetry,
    skeleton = 'rows',
    skeletonCount,
    empty,
    errorTitle,
    errorLead,
    helpPath,
    className,
    children,
  } = props;

  if (loading) {
    return (
      <div className={className}>
        <DataStateSkeleton variant={skeleton} count={skeletonCount} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <DataStateError
          title={errorTitle}
          lead={errorLead}
          detail={errorDetailOf(error)}
          onRetry={onRetry}
          helpPath={helpPath}
        />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className={className}>
        <EmptyState
          title={empty?.title ?? <FormattedMessage id="DataState.emptyTitle" />}
          lead={empty?.lead ?? <FormattedMessage id="DataState.emptyLead" />}
          action={empty?.action}
          icon={empty?.icon}
        />
      </div>
    );
  }

  return <>{children}</>;
};

const { bool, func, node, number, object, oneOf, oneOfType, shape, string } = PropTypes;

DataState.propTypes = {
  loading: bool,
  // Any truthy failure: an SDK error, an Error, a string, or just `true`.
  error: oneOfType([bool, object, string]),
  isEmpty: bool,
  onRetry: func,
  skeleton: oneOf(['cards', 'rows', 'block']),
  skeletonCount: number,
  empty: shape({
    title: node,
    lead: node,
    icon: node,
    action: shape({ label: node, href: string, onClick: func, variant: string }),
  }),
  errorTitle: node,
  errorLead: node,
  helpPath: string,
  className: string,
  children: node,
};

export default DataState;
